import React, { useEffect, useState } from "react";
import { navigate } from "gatsby";
import Link from "./Link";
import { useQueryParam, StringParam, NumberParam } from "use-query-params";
import { Row, Col } from "react-bootstrap";

import { intelligenceCompData } from "../data/components-intelligence.data";
import ChevronRight from "../images/chevron-right.svg";
import { TabLabels } from "./Tabs";
import ButtonText from "./ButtonText";
import {
    getDateRangeString,
    getPlaceString,
    getContactString,
} from "../helpers/formatHelpers";

const Articles = ({
    showPostsCountInitial = 6,
    loadCount = 6,
    temp = "news",
    pagination,
}) => {
    const compData = intelligenceCompData();

    const [activeParentCat, setActiveParentCat] = useQueryParam(
        "parentcat",
        StringParam
    );

    const [activeCat, setActiveCat] = useQueryParam("cat", StringParam);

    const currentParentCat = activeParentCat || temp;

    const tabs = [
        { name: "all", id: undefined },
        ...compData.allWpCategory.nodes
            .filter((node) => {
                return (
                    node.count > 0 &&
                    node.wpParent &&
                    node.wpParent.node.slug === currentParentCat
                );
            })
            .map((node) => ({
                ...node,
                name: node.name,
                id: node.slug,
                order: Number(node.description) || 0,
            }))
            .sort((a, b) => a.order - b.order),
    ];

    const articles = compData.allWpPost.nodes
        .filter((post) => {
            return compData.allSitePage.nodes.find(
                (sitePage) => sitePage.context.id === post.id
            );
        })
        .map((post) => {
            const localPage = compData.allSitePage.nodes.find(
                (sitePage) => sitePage.context.id === post.id
            );

            const mainCategory =
                post.categories.nodes.find(
                    (category) =>
                        category.wpParent?.node?.slug === currentParentCat
                ) || post.categories.nodes[0];

            return {
                id: post.id,
                category: mainCategory.name,
                dateStart:
                    post.eventPost && post.eventPost.dateStart
                        ? post.eventPost.dateStart
                        : post.date,
                dateEnd:
                    post.eventPost && post.eventPost.dateEnd
                        ? post.eventPost.dateEnd
                        : "",
                place:
                    post.eventPost && post.eventPost.place
                        ? post.eventPost.place
                        : "",
                contact:
                    post.eventPost && post.eventPost.contact
                        ? post.eventPost.contact
                        : "",
                title: post.title,
                text: post.excerpt,
                cat: mainCategory.slug,
                parentCat: mainCategory.wpParent?.node?.slug,
                url: localPage.path,
            };
        });

    const [showPostsCount, setShowPostsCount] = useQueryParam(
        "showPostsCount",
        NumberParam
    );

    const [preventScroll, setPreventScroll] = useQueryParam(
        "prsc",
        StringParam
    );

    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        setIsLoaded(true);
    }, []);

    const articlesForParentCategory = articles.filter(
        (article) => article.parentCat === currentParentCat
    );

    const hasPagination =
        Number.isInteger(pagination?.skip) &&
        Number.isInteger(pagination?.limit);

    const articlesFiltered = articlesForParentCategory.filter(
        (article) => !activeCat || article.cat === activeCat
    );

    const articlesForPage = hasPagination
        ? articlesFiltered.slice(
              pagination.skip,
              pagination.skip + pagination.limit
          )
        : articlesFiltered;

    const articlesCurrent = hasPagination
        ? articlesForPage
        : articlesForPage.slice(0, showPostsCount || showPostsCountInitial);

    const paginationTotalPages = hasPagination
        ? Math.max(1, Math.ceil(articlesFiltered.length / pagination.limit))
        : 1;

    const handleTabClick = ({ id }) => {
        if (hasPagination) {
            const filterParams = id
                ? `?cat=${encodeURIComponent(id)}&prsc=true`
                : "?prsc=true";

            navigate(`${pagination.archivePath}${filterParams}`);
            return;
        }

        setPreventScroll("true", "replaceIn");
        setShowPostsCount(undefined, "replaceIn");
        setActiveCat(id, "replaceIn");
    };

    const handleLoadMore = () => {
        setShowPostsCount(articlesCurrent.length + loadCount, "replaceIn");
        setPreventScroll("true", "replaceIn");
    };

    return (
        <div className="articles articles--cards articles--news-layout">
            <TabLabels
                className="py-2 -filter-tags"
                buttonVariant="small,deepblue"
                labels={tabs.map((item) => ({
                    label: item.name,
                    active: item.id === activeCat || (!item.id && !activeCat),
                    onClick: () => handleTabClick({ id: item.id }),
                }))}
            />

            <Row className="news-and-insights__cards-row news-and-insights__cards-row--vertical pt-5">
                {articlesCurrent.map((article, index) => (
                    <Col key={article.id} xs={24} lg={8}>
                        <Article
                            className={
                                isLoaded &&
                                (!activeCat || activeCat === article.cat)
                                    ? ""
                                    : "d-none"
                            }
                            style={{
                                animationDelay: `${
                                    index < 10 ? index * 0.1 : 1
                                }s`,
                            }}
                            {...article}
                        />
                    </Col>
                ))}
            </Row>

            {!hasPagination &&
                isLoaded &&
                articlesFiltered.length > articlesCurrent.length && (
                    <Row className="news-and-insights__footer-row">
                        <ButtonText
                            variant="red-filled"
                            onClick={handleLoadMore}
                        >
                            Load more
                        </ButtonText>
                    </Row>
                )}

            {hasPagination && paginationTotalPages > 1 && (
                <ArchivePagination
                    {...pagination}
                    totalPages={paginationTotalPages}
                    activeCat={activeCat}
                    activeParentCat={activeParentCat}
                />
            )}
        </div>
    );
};

const ArchivePagination = ({
    currentPage,
    totalPages,
    archivePath,
    activeCat,
    activeParentCat,
}) => {
    const filterParams = [
        activeParentCat
            ? `parentcat=${encodeURIComponent(activeParentCat)}`
            : null,
        activeCat ? `cat=${encodeURIComponent(activeCat)}` : null,
        activeCat || activeParentCat ? "prsc=true" : null,
    ]
        .filter(Boolean)
        .join("&");

    const getPagePath = (pageNumber) => {
        const pagePath =
            pageNumber === 1
                ? archivePath
                : `${archivePath}page/${pageNumber}/`;

        return filterParams ? `${pagePath}?${filterParams}` : pagePath;
    };

    const paginationItems = getPaginationItems(currentPage, totalPages);

    return (
        <nav className="articles__pagination" aria-label="News pagination">
            {currentPage > 1 && (
                <Link
                    to={getPagePath(currentPage - 1)}
                    className="articles__pagination-direction"
                    rel="prev"
                    aria-label="Previous page"
                >
                    <ChevronRight
                        className="articles__pagination-arrow -previous"
                        aria-hidden="true"
                    />
                </Link>
            )}

            <div className="articles__pagination-pages">
                {paginationItems.map((item) =>
                    typeof item === "number" ? (
                        <Link
                            key={item}
                            to={getPagePath(item)}
                            className={`articles__pagination-page ${
                                item === currentPage ? "-active" : ""
                            }`}
                            aria-current={
                                item === currentPage ? "page" : undefined
                            }
                            aria-label={`Page ${item}`}
                        >
                            {item}
                        </Link>
                    ) : (
                        <span
                            key={item}
                            className="articles__pagination-ellipsis"
                            aria-hidden="true"
                        >
                            &hellip;
                        </span>
                    )
                )}
            </div>

            {currentPage < totalPages && (
                <Link
                    to={getPagePath(currentPage + 1)}
                    className="articles__pagination-direction"
                    rel="next"
                    aria-label="Next page"
                >
                    <ChevronRight
                        className="articles__pagination-arrow"
                        aria-hidden="true"
                    />
                </Link>
            )}
        </nav>
    );
};

const getPaginationItems = (currentPage, totalPages) => {
    if (totalPages <= 8) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    if (currentPage <= 5) {
        return [1, 2, 3, 4, 5, 6, "ellipsis-right", totalPages];
    }

    if (currentPage >= totalPages - 4) {
        return [
            1,
            "ellipsis-left",
            totalPages - 5,
            totalPages - 4,
            totalPages - 3,
            totalPages - 2,
            totalPages - 1,
            totalPages,
        ];
    }

    return [
        1,
        "ellipsis-left",
        currentPage - 1,
        currentPage,
        currentPage + 1,
        "ellipsis-right",
        totalPages,
    ];
};
const cleanExcerpt = (text = "") => {
    return text
        .replace(
            /\[(?:table_of_contents|content_quote|content_definition|content_takeaways|content_anchor)\b[^\]]*\]/gi,
            ""
        )
        .trim();
};
const Article = ({
    className = "",
    category,
    dateStart,
    dateEnd,
    place,
    title,
    text,
    url,
    parentCat,
    contact,
    ...attrs
}) => (
    <div className={`news-and-insights__card ${className}`} {...attrs}>
        <div className="news-and-insights__card-content">
            {url ? (
                <Link to={url} className="news-and-insights__link">
                    {title}
                </Link>
            ) : (
                <h3 className="news-and-insights__link">{title}</h3>
            )}

            <div className="news-and-insights__card-meta">
                <div className="news-and-insights__date">
                    {getDateRangeString(dateStart, dateEnd)}
                </div>
                <span style={{ marginRight: "13px" }}></span>|
                <span style={{ marginLeft: "13px" }}></span>
                {getPlaceString(place, category) && (
                    <div className="news-and-insights__date">
                        {getPlaceString(place, category)}
                    </div>
                )}
            </div>

            {text && (
                <div
                    className="news-and-insights__excerpt"
                    dangerouslySetInnerHTML={{ __html: cleanExcerpt(text) }}
                />
            )}

            {getContactString(contact)}
        </div>

        <div className="news-and-insights__card-notch">
            {url ? (
                <Link
                    to={url}
                    className="news-and-insights__action-button"
                    aria-label="Read full article"
                >
                    <ArrowIcon />
                </Link>
            ) : (
                <span className="news-and-insights__action-button">
                    <ArrowIcon />
                </span>
            )}
        </div>
    </div>
);

const ArrowIcon = () => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="7" y1="17" x2="17" y2="7" />
        <polyline points="7 7 17 7 17 17" />
    </svg>
);

export default Articles;
